// @ts-nocheck
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, ArrowRight, Scale, Users, DollarSign, ChevronDown, ChevronUp, AlertCircle, FileSpreadsheet, Table } from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import { SessionExpiredError } from '../../utils/sessionExpiredError';
import './ReconUpload.css';

const ReconUpload = ({ onError, loading: externalLoading, userEmail }) => {
  // Payroll state
  const [payrollFile, setPayrollFile] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollResult, setPayrollResult] = useState(null);
  
  // P&L state
  const [plFile, setPlFile] = useState(null);
  const [plSheets, setPlSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [plLoading, setPlLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [plDiscovery, setPlDiscovery] = useState(null);
  const [plWagesResult, setPlWagesResult] = useState(null);
  const [selectedWagesRowIndices, setSelectedWagesRowIndices] = useState([]);
  const [selectedPLPeriodIndex, setSelectedPLPeriodIndex] = useState(null);

  const reconLoading = payrollLoading || plLoading;
  
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    payrollSummary: true,
    employees: false,
    plWages: true,
    rawJson: false
  });

  // Payroll dropzone
  const onDropPayroll = useCallback((acceptedFiles) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    const validTypes = ['.pdf', '.png', '.jpg', '.jpeg'];
    const isValid = validTypes.some(ext => droppedFile.name.toLowerCase().endsWith(ext));
    
    if (!isValid) {
      onError('Please upload a PDF or image file (.pdf, .png, .jpg, .jpeg)');
      return;
    }

    setPayrollFile(droppedFile);
    setPayrollResult(null);
  }, [onError]);

  const { getRootProps: getPayrollRootProps, getInputProps: getPayrollInputProps, isDragActive: isPayrollDragActive } = useDropzone({
    onDrop: onDropPayroll,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    maxFiles: 1,
    disabled: reconLoading || externalLoading
  });

  // P&L dropzone
  const onDropPL = useCallback(async (acceptedFiles) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    const validTypes = ['.xlsx', '.xlsm', '.csv'];
    const isValid = validTypes.some(ext => droppedFile.name.toLowerCase().endsWith(ext));

    if (!isValid) {
      onError('Please upload an Excel file (.xlsx, .xlsm) or CSV file (.csv)');
      return;
    }

    setPlFile(droppedFile);
    setPlDiscovery(null);
    setPlWagesResult(null);
    setSelectedSheet('');

    // Fetch sheets from the file
    try {
      await supabase.auth.getSession();
      
      const formData = new FormData();
      formData.append('file', droppedFile);

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/get-sheets/`,
        formData,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': import.meta.env.VITE_API_KEY,
          },
        }
      );

      setPlSheets(response.data.sheets || []);
      if (response.data.sheets?.length > 0) {
        setSelectedSheet(response.data.sheets[0]);
      }
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('Failed to get sheets:', error);
      onError('Failed to read Excel file sheets');
    }
  }, [onError]);

  const { getRootProps: getPlRootProps, getInputProps: getPlInputProps, isDragActive: isPlDragActive } = useDropzone({
    onDrop: onDropPL,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: reconLoading || externalLoading
  });

  const removePayrollFile = () => {
    setPayrollFile(null);
    setPayrollResult(null);
  };

  const removePlFile = () => {
    setPlFile(null);
    setPlSheets([]);
    setSelectedSheet('');
    setPlDiscovery(null);
    setPlWagesResult(null);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleRunRecon = async () => {
    if (!payrollFile) {
      onError('Please upload a payroll register file');
      return;
    }
    if (!plFile || !selectedSheet) {
      onError('Please upload a P&L file and select a sheet');
      return;
    }

    // reset previous results on each run
    setPayrollResult(null);
    setPlWagesResult(null);

    setPayrollLoading(true);
    setPlLoading(true);

    try {
      const headers = {
        'Content-Type': 'multipart/form-data',
        'x-api-key': import.meta.env.VITE_API_KEY,
      };

      const runPayroll = async () => {
        const formData = new FormData();
        formData.append('file', payrollFile);
        formData.append('include_raw_markdown', 'false');

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/recon/extract-payroll/`,
          formData,
          { headers, withCredentials: true, timeout: 300000 }
        );

        setPayrollResult(response.data);
        if (response.data?.success) {
          setExpandedSections(prev => ({ ...prev, payrollSummary: true, employees: true }));
        } else {
          onError(response.data?.error || 'Payroll extraction failed');
        }
      };

      const runPLWages = async () => {
        const discoveryFormData = new FormData();
        discoveryFormData.append('file', plFile);
        discoveryFormData.append('sheet_name', selectedSheet);

        const discoveryResponse = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/discover-periods/`,
          discoveryFormData,
          { headers, withCredentials: true, timeout: 120000 }
        );

        const discovery = discoveryResponse.data;
        setPlDiscovery(discovery);

        const wagesFormData = new FormData();
        wagesFormData.append('file', plFile);
        wagesFormData.append('sheet_name', selectedSheet);
        wagesFormData.append('period_options', JSON.stringify(discovery.period_options));
        wagesFormData.append('account_name_column', discovery.account_name_column.toString());
        wagesFormData.append('header_start_row', discovery.header_start_row.toString());
        wagesFormData.append('header_end_row', discovery.header_end_row.toString());

        const wagesResponse = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/recon/extract-pl-wages/`,
          wagesFormData,
          { headers, withCredentials: true, timeout: 120000 }
        );

        setPlWagesResult(wagesResponse.data);
        if (wagesResponse.data?.success) {
          setExpandedSections(prev => ({ ...prev, plWages: true }));
        } else {
          onError(wagesResponse.data?.error || 'P&L wages extraction failed');
        }
      };

      await Promise.all([runPayroll(), runPLWages()]);
    } catch (error) {
      if (error instanceof SessionExpiredError) return;
      logger.error('Recon run error:', error);
      onError(error.response?.data?.detail || error.message || 'Recon run failed');
    } finally {
      setPayrollLoading(false);
      setPlLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleReset = () => {
    setPayrollFile(null);
    setPayrollResult(null);
    setPlFile(null);
    setPlSheets([]);
    setSelectedSheet('');
    setPlDiscovery(null);
    setPlWagesResult(null);
    setExpandedSections({
      payrollSummary: true,
      employees: false,
      plWages: true,
      rawJson: false
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const extraction = payrollResult?.extraction;
  const wages = plWagesResult?.wages;

  const wagesCandidates = useMemo(() => {
    const c = plWagesResult?.candidates;
    if (Array.isArray(c) && c.length > 0) return c;
    // Backward compatibility: if backend only returns wages
    if (wages) return [{ ...wages, rank: 1, confidence: null, is_total_like: false }];
    return [];
  }, [plWagesResult, wages]);

  const parseUsDate = (s) => {
    if (!s) return null;
    const parts = String(s).trim().split('/');
    if (parts.length !== 3) return null;
    const [mm, dd, yyyy] = parts.map(p => parseInt(p, 10));
    if (!mm || !dd || !yyyy) return null;
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d.getTime()) ? null : d;
  };

  const parsePeriodLabelToMonth = (label) => {
    // Tries to map a P&L period label to a month/year anchor date.
    // Supports: "Jan 2024", "Jan-2024", "2024-01", "2024-01-31"
    if (!label) return null;
    const t = String(label).trim();
    const lower = t.toLowerCase();
    const monthNames = {
      jan: 0, january: 0,
      feb: 1, february: 1,
      mar: 2, march: 2,
      apr: 3, april: 3,
      may: 4,
      jun: 5, june: 5,
      jul: 6, july: 6,
      aug: 7, august: 7,
      sep: 8, september: 8,
      oct: 9, october: 9,
      nov: 10, november: 10,
      dec: 11, december: 11,
    };

    let m = lower.match(/^([a-z]+)\s+(\d{4})$/);
    if (m) {
      const mon = monthNames[m[1]];
      if (mon !== undefined) return new Date(parseInt(m[2], 10), mon, 1);
    }

    m = lower.match(/^([a-z]+)-(\d{4})$/);
    if (m) {
      const mon = monthNames[m[1]];
      if (mon !== undefined) return new Date(parseInt(m[2], 10), mon, 1);
    }

    m = lower.match(/^(\d{4})-(\d{1,2})$/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, 1);

    m = lower.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, 1);

    const d = new Date(t);
    if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), 1);
    return null;
  };

  const getPayrollAnchorDate = () => {
    const p = extraction?.pay_period;
    return parseUsDate(p?.period_end_date) || parseUsDate(p?.check_date) || parseUsDate(p?.period_begin_date) || null;
  };

  const pickDefaultPeriodIndex = (valuesByPeriod) => {
    if (!Array.isArray(valuesByPeriod) || valuesByPeriod.length === 0) return null;

    const payrollDate = getPayrollAnchorDate();
    if (payrollDate) {
      const match = valuesByPeriod.find(v => {
        const d = parsePeriodLabelToMonth(v.period_name);
        return d && d.getFullYear() === payrollDate.getFullYear() && d.getMonth() === payrollDate.getMonth();
      });
      if (match) return match.period_index;
    }

    const firstWithAmount = valuesByPeriod.find(v => v.amount !== null && v.amount !== undefined);
    return (firstWithAmount ? firstWithAmount.period_index : valuesByPeriod[0].period_index);
  };

  // Initialize defaults when new results arrive (new recon run)
  useEffect(() => {
    if (!plWagesResult) return;
    const defaultRow =
      plWagesResult?.selected_row_index ??
      plWagesResult?.wages?.row_index ??
      (wagesCandidates[0]?.row_index ?? null);
    if (wagesCandidates.length === 1 && wagesCandidates[0]?.row_index != null) {
      setSelectedWagesRowIndices([wagesCandidates[0].row_index]);
    } else if (defaultRow != null) {
      setSelectedWagesRowIndices([defaultRow]);
    } else {
      setSelectedWagesRowIndices([]);
    }

    const candidate = wagesCandidates.find(c => c.row_index === defaultRow) || wagesCandidates[0];
    const defaultPeriod = pickDefaultPeriodIndex(candidate?.values_by_period);
    setSelectedPLPeriodIndex(defaultPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plWagesResult]);

  const selectedCandidates = useMemo(() => {
    if (!Array.isArray(selectedWagesRowIndices) || selectedWagesRowIndices.length === 0) return [];
    const set = new Set(selectedWagesRowIndices);
    return wagesCandidates.filter(c => set.has(c.row_index));
  }, [wagesCandidates, selectedWagesRowIndices]);

  const selectedPeriodName = useMemo(() => {
    const basis = selectedCandidates[0] || wagesCandidates[0] || wages;
    if (!basis || selectedPLPeriodIndex == null) return null;
    const pv = basis.values_by_period?.find(p => p.period_index === selectedPLPeriodIndex);
    return pv?.period_name || null;
  }, [selectedCandidates, wagesCandidates, wages, selectedPLPeriodIndex]);

  const getCandidateAmountForPeriod = (candidate, periodIndex) => {
    if (!candidate || periodIndex == null) return null;
    const pv = candidate.values_by_period?.find(p => p.period_index === periodIndex);
    const v = pv?.amount;
    return (typeof v === 'number') ? v : (v == null ? null : Number(v));
  };

  const plSelectedAmount = useMemo(() => {
    if (selectedPLPeriodIndex == null) return null;
    if (!selectedCandidates || selectedCandidates.length === 0) return null;
    const values = selectedCandidates
      .map(c => getCandidateAmountForPeriod(c, selectedPLPeriodIndex))
      .filter(v => typeof v === 'number' && !Number.isNaN(v));
    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0);
  }, [selectedCandidates, selectedPLPeriodIndex]);

  const payrollTotal = extraction?.total_gross_wages ?? null;
  const diffAmount = (payrollTotal != null && plSelectedAmount != null) ? (plSelectedAmount - payrollTotal) : null;
  const diffPct = (payrollTotal != null && plSelectedAmount != null && payrollTotal !== 0)
    ? (diffAmount / payrollTotal)
    : null;

  return (
    <div className="recon-page-container">
      {/* Payroll Section */}
      <div className={`recon-upload-container border-beam-container ${payrollLoading ? 'loading' : ''}`}>
        <div className="border-beam"></div>
        
        <div className="upload-header">
          <div className="header-icon recon-icon">
            <Scale size={24} />
          </div>
          <div>
            <h3>Payroll Register</h3>
            <p className="upload-description">
              Upload a payroll register PDF to extract employee earnings data
            </p>
          </div>
        </div>

        {!payrollResult ? (
          <div className="upload-form">
            <div
              {...getPayrollRootProps()}
              className={`dropzone ${isPayrollDragActive ? 'active' : ''} ${payrollFile ? 'has-files' : ''} ${reconLoading ? 'disabled' : ''}`}
            >
              <input {...getPayrollInputProps()} />
              <div className="dropzone-content">
                <Upload size={36} className="upload-icon" />
                <p className="dropzone-text">
                  {isPayrollDragActive ? "Drop file here..." : "Drag & drop a payroll register PDF"}
                </p>
                <span className="dropzone-hint">or click to browse • PDF, PNG, JPG supported</span>
              </div>
            </div>

            {payrollFile && (
              <div className="file-card">
                <div className="file-card-header">
                  <div className="file-card-info">
                    <FileText size={20} className="file-icon" />
                    <div className="file-details">
                      <p className="file-name">{payrollFile.name}</p>
                      <p className="file-meta">{(payrollFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={removePayrollFile} 
                    className="remove-file-btn"
                    disabled={reconLoading}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="extraction-results">
            {/* Summary Section */}
            <div className="result-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('payrollSummary')}
              >
                <div className="section-title">
                  <DollarSign size={18} />
                  <span>Payroll Summary</span>
                </div>
                {expandedSections.payrollSummary ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              
              {expandedSections.payrollSummary && extraction && (
                <div className="section-content">
                  <div className="summary-list">
                    <div className="summary-row">
                      <span className="summary-label">Pay Period</span>
                      <span className="summary-value">
                        {extraction.pay_period?.period_begin_date && extraction.pay_period?.period_end_date
                          ? `${extraction.pay_period.period_begin_date} – ${extraction.pay_period.period_end_date}`
                          : extraction.pay_period?.check_date 
                            ? extraction.pay_period.check_date
                            : 'Not specified'}
                      </span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Employees</span>
                      <span className="summary-value">{extraction.employee_count}</span>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Pages Processed</span>
                      <span className="summary-value">{extraction.page_count}</span>
                    </div>
                    <div className="summary-row highlight-row">
                      <span className="summary-label">Total Gross Wages</span>
                      <span className="summary-value highlight">
                        {formatCurrency(extraction.total_gross_wages)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Employees Section */}
            <div className="result-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('employees')}
              >
                <div className="section-title">
                  <Users size={18} />
                  <span>Employee Details ({extraction?.employees?.length || 0})</span>
                </div>
                {expandedSections.employees ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              
              {expandedSections.employees && extraction?.employees && (
                <div className="section-content">
                  <div className="employees-list">
                    {extraction.employees.map((emp, idx) => (
                      <div key={idx} className="employee-card">
                        <div className="employee-header">
                          <div className="employee-info">
                            <span className="employee-name">{emp.name}</span>
                            <span className="employee-number">#{emp.employee_number}</span>
                          </div>
                          <span className="employee-gross">
                            {formatCurrency(emp.gross_wage || emp.current_earnings?.reduce((sum, e) => sum + e.amount, 0) || 0)}
                          </span>
                        </div>
                        {emp.current_earnings && emp.current_earnings.length > 0 && (
                          <div className="employee-earnings">
                            {emp.current_earnings.map((earning, eIdx) => (
                              <div key={eIdx} className="employee-earning-item">
                                <span>{earning.type}</span>
                                <span>{formatCurrency(earning.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* P&L Section */}
      <div className={`recon-upload-container border-beam-container ${plLoading ? 'loading' : ''}`}>
        <div className="border-beam"></div>
        
        <div className="upload-header">
          <div className="header-icon pl-icon">
            <FileSpreadsheet size={24} />
          </div>
          <div>
            <h3>P&L Statement</h3>
            <p className="upload-description">
              Upload a P&L Excel file to extract wages line item
            </p>
          </div>
        </div>

        {!plWagesResult ? (
          <div className="upload-form">
            <div
              {...getPlRootProps()}
              className={`dropzone ${isPlDragActive ? 'active' : ''} ${plFile ? 'has-files' : ''} ${reconLoading ? 'disabled' : ''}`}
            >
              <input {...getPlInputProps()} />
              <div className="dropzone-content">
                <Upload size={36} className="upload-icon" />
                <p className="dropzone-text">
                  {isPlDragActive ? "Drop file here..." : "Drag & drop a P&L Excel file"}
                </p>
                <span className="dropzone-hint">or click to browse • .xlsx, .xlsm, .csv supported</span>
              </div>
            </div>

            {plFile && (
              <>
                <div className="file-card">
                  <div className="file-card-header">
                    <div className="file-card-info">
                      <FileSpreadsheet size={20} className="file-icon" />
                      <div className="file-details">
                        <p className="file-name">{plFile.name}</p>
                        <p className="file-meta">{(plFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={removePlFile} 
                      className="remove-file-btn"
                      disabled={reconLoading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {plSheets.length > 0 && (
                  <div className="sheet-selector">
                    <label htmlFor="sheet-select">Select Sheet:</label>
                    <select
                      id="sheet-select"
                      value={selectedSheet}
                      onChange={(e) => setSelectedSheet(e.target.value)}
                      disabled={reconLoading}
                    >
                      {plSheets.map((sheet, idx) => (
                        <option key={idx} value={sheet}>{sheet}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="extraction-results">
            {/* P&L Wages Section */}
            <div className="result-section">
              <button 
                className="section-header"
                onClick={() => toggleSection('plWages')}
              >
                <div className="section-title">
                  <Table size={18} />
                  <span>P&L Wages</span>
                </div>
                {expandedSections.plWages ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
              
              {expandedSections.plWages && wages && (
                <div className="section-content">
                  {/* Candidate selection (supports select/unselect) */}
                  {wagesCandidates.length > 1 && (
                    <div className="wages-candidate-selector">
                      <div className="wages-candidate-header">
                        <div className="wages-candidate-title">Select the wages line item</div>
                      </div>
                      <div className="wages-candidate-table-container">
                        <table className="wages-candidate-table">
                          <thead>
                            <tr>
                              <th>Pick</th>
                              <th>Account</th>
                              <th>Row</th>
                            </tr>
                          </thead>
                          <tbody>
                            {wagesCandidates
                              .slice()
                              .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
                              .map((c) => {
                                const isSelected = selectedWagesRowIndices.includes(c.row_index);
                                return (
                                  <tr
                                    key={`${c.row_index}-${c.rank}`}
                                    className={isSelected ? 'candidate-selected' : ''}
                                    onClick={() => {
                                      setSelectedWagesRowIndices(prev => {
                                        const arr = Array.isArray(prev) ? prev : [];
                                        if (arr.includes(c.row_index)) return arr.filter(x => x !== c.row_index);
                                        return [...arr, c.row_index];
                                      });
                                      if (selectedPLPeriodIndex == null) {
                                        const def = pickDefaultPeriodIndex(c.values_by_period);
                                        setSelectedPLPeriodIndex(def);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    <td>
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={`Select ${c.account_name} (row ${c.row_index})`}
                                        className="candidate-checkbox"
                                        onChange={() => {
                                          // Prevent row click from toggling twice
                                          setSelectedWagesRowIndices(prev => {
                                            const arr = Array.isArray(prev) ? prev : [];
                                            if (arr.includes(c.row_index)) return arr.filter(x => x !== c.row_index);
                                            return [...arr, c.row_index];
                                          });
                                          if (selectedPLPeriodIndex == null) {
                                            const def = pickDefaultPeriodIndex(c.values_by_period);
                                            setSelectedPLPeriodIndex(def);
                                          }
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <div className="candidate-name">
                                        {c.account_name}
                                        {c.is_total_like && <span className="candidate-pill">Total/Subtotal</span>}
                                      </div>
                                    </td>
                                    <td className="candidate-mono">#{c.row_index}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Selected candidate + period selection + reconcile */}
                  <div className="wages-info">
                    <div className="wages-account">
                      <span className="wages-label">Selected Accounts:</span>
                      <span className="wages-account-name">
                        {selectedCandidates.length > 0 ? `${selectedCandidates.length} selected` : (wages?.account_name ? '1 selected' : 'None selected')}
                      </span>
                    </div>
                    {wages?.reasoning && (
                      <p className="wages-reasoning">{wages.reasoning}</p>
                    )}
                  </div>
                  
                  <div className="wages-period-stack">
                    <div className="wages-period-selector">
                      <label htmlFor="pl-period-select">Which P&amp;L period should we reconcile to this payroll?</label>
                      <select
                        id="pl-period-select"
                        value={selectedPLPeriodIndex ?? ''}
                        onChange={(e) => setSelectedPLPeriodIndex(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                        disabled={selectedWagesRowIndices.length === 0}
                      >
                        <option value="">Select a period…</option>
                        {((selectedCandidates[0]?.values_by_period) || (wagesCandidates[0]?.values_by_period) || wages?.values_by_period || []).map((p) => (
                          <option key={p.period_index} value={p.period_index}>
                            {p.period_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="wages-reconcile-row">
                      <div className="wages-reconcile-card">
                        <div className="reconcile-title">Reconcile (Payroll vs P&amp;L)</div>
                        <div className="reconcile-grid">
                          <div className="reconcile-item">
                            <div className="reconcile-label">Payroll gross (PDF)</div>
                            <div className="reconcile-value">{formatCurrency(payrollTotal)}</div>
                          </div>
                          <div className="reconcile-item">
                            <div className="reconcile-label">P&amp;L wages (selected items)</div>
                            <div className="reconcile-value">{formatCurrency(plSelectedAmount)}</div>
                          </div>
                          <div className="reconcile-item">
                            <div className="reconcile-label">Difference</div>
                            <div className={`reconcile-value ${diffAmount != null && diffAmount !== 0 ? 'reconcile-diff' : ''}`}>
                              {diffAmount == null ? 'N/A' : formatCurrency(diffAmount)}
                              {diffPct != null && (
                                <span className="reconcile-pct">
                                  ({(diffPct * 100).toFixed(1)}%)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="reconcile-hint">
                          Selected period: {selectedPeriodName || '—'} • Selected items: {selectedCandidates.length}
                          <br />
                          Payroll period: {extraction?.pay_period?.period_begin_date && extraction?.pay_period?.period_end_date
                            ? `${extraction.pay_period.period_begin_date} – ${extraction.pay_period.period_end_date}`
                            : extraction?.pay_period?.check_date || 'Not specified'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Single action button (runs both payroll + P&L wages) */}
      <div className="result-actions">
        <button
          type="button"
          className="analyze-button btn-animated"
          onClick={handleRunRecon}
          disabled={reconLoading || !payrollFile || !plFile || !selectedSheet}
        >
          {reconLoading ? (
            <>
              <div className="button-spinner"></div>
              Running recon...
            </>
          ) : (
            <>
              Run Recon
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>

      {/* Errors */}
      {payrollResult && !payrollResult.success && (
        <div className="extraction-error">
          <AlertCircle size={20} />
          <span>{payrollResult.error}</span>
        </div>
      )}
      {plWagesResult && !plWagesResult.success && (
        <div className="extraction-error">
          <AlertCircle size={20} />
          <span>{plWagesResult.error}</span>
        </div>
      )}
    </div>
  );
};

export default ReconUpload;
